import { useLocalizedPath } from "../i18n/useLocalizedPath";
import React from "react";
import { Box, Flex, Grid, Heading, Image, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import MaskedReveal from "./MaskedReveal";

const brandOriginImage =
  "https://assets.deepsoulai.com/main-page/brand-origin/family_photo_album.png";

const HomeStoryIntro: React.FC = () => {
  const { t } = useTranslation();
  const localizePath = useLocalizedPath();
  const keywordKeys = ["record", "understand", "share"];

  return (
    <Box
      as="section"
      id="the-beginning"
      data-testid="story-chapter-beginning"
      scrollMarginTop="88px"
      overflow="hidden"
      bg="radial-gradient(circle at 7% 10%, rgba(217, 238, 251, 0.82), transparent 34rem), radial-gradient(circle at 93% 82%, rgba(255, 220, 222, 0.72), transparent 36rem)"
    >
      <Box
        maxW="1400px"
        mx="auto"
        px={{ base: 4, md: 8 }}
        py={{ base: 14, md: 20, lg: 24 }}
      >
        <Grid
          templateColumns={{ base: "1fr", lg: "0.72fr 1.28fr" }}
          gap={{ base: 5, lg: 16 }}
          alignItems="start"
          mb={{ base: 9, md: 14 }}
        >
          <MaskedReveal>
            <Flex
              align="center"
              gap={3}
              color="rgba(37, 39, 45, 0.58)"
              letterSpacing="0.12em"
              textTransform="uppercase"
            >
              <Text aria-hidden="true" color="#25272D" fontWeight="700">
                01
              </Text>
              <Heading
                as="h2"
                fontSize={{ base: "sm", md: "md" }}
                fontWeight="700"
                letterSpacing="0.12em"
              >
                {t("homeStory.beginning.title")}
              </Heading>
            </Flex>
          </MaskedReveal>

          <MaskedReveal delay={0.08}>
            <Heading
              as="h3"
              color="#25272D"
              fontSize={{ base: "3xl", md: "5xl" }}
              lineHeight={{ base: 1.3, md: 1.16 }}
              letterSpacing="-0.04em"
              maxW={{ base: "100%", md: "20ch" }}
              sx={{ textWrap: "balance" }}
            >
              {t("homeStory.beginning.content")}
            </Heading>
          </MaskedReveal>
        </Grid>

        <Grid
          templateColumns={{
            base: "1fr",
            lg: "minmax(0, 0.88fr) minmax(420px, 1.12fr)",
          }}
          gap={{ base: 9, lg: 14 }}
          alignItems="stretch"
        >
          <MaskedReveal delay={0.1}>
            <Flex
              h="100%"
              direction="column"
              align="flex-start"
              justify="center"
              py={{ base: 0, lg: 8 }}
            >
              <Text
                color="gray.700"
                fontSize={{ base: "md", md: "lg" }}
                lineHeight={{ base: 1.85, md: 1.95 }}
                maxW="54ch"
              >
                {t("homeStory.beginning.body")}
              </Text>

              <Flex wrap="wrap" gap={3} mt={7}>
                {keywordKeys.map((key) => (
                  <Text
                    key={key}
                    data-testid={`brand-origin-keyword-${key}`}
                    px={4}
                    py={2}
                    borderRadius="full"
                    bg="rgba(255,255,255,.76)"
                    color="#25272D"
                    fontSize="sm"
                    fontWeight="700"
                    border="1px solid"
                    borderColor="blackAlpha.200"
                  >
                    {t(`homeStory.beginning.keywords.${key}`)}
                  </Text>
                ))}
              </Flex>

              <Box
                as={RouterLink}
                to={localizePath("/about#company-story")}
                display="inline-flex"
                alignItems="center"
                gap={2}
                mt={9}
                pb={1}
                color="#25272D"
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="700"
                borderBottom="1px solid"
                borderColor="currentColor"
                _hover={{ color: "primary.600", gap: 3 }}
                _focusVisible={{
                  outline: "3px solid",
                  outlineColor: "primary.400",
                  outlineOffset: "5px",
                }}
                transition="gap 180ms ease"
              >
                {t("homeStory.beginning.cta")}
                <Box as="span" aria-hidden="true">
                  →
                </Box>
              </Box>
            </Flex>
          </MaskedReveal>

          <MaskedReveal delay={0.16}>
            <Box
              data-testid="brand-origin-image-frame"
              data-aspect-ratio="4:3"
              as="figure"
              position="relative"
              m={0}
              aspectRatio="4 / 3"
              overflow="hidden"
              borderRadius={{ base: "2xl", md: "3xl" }}
              bg="gray.100"
              boxShadow="0 26px 70px rgba(42,55,68,.13)"
            >
              <Image
                data-testid="brand-origin-image"
                src={brandOriginImage}
                alt={t("homeStory.beginning.imageAlt")}
                w="100%"
                h="100%"
                position="absolute"
                inset={0}
                objectFit="cover"
                objectPosition="center 45%"
              />
              <Box
                position="absolute"
                inset={0}
                bg="linear-gradient(180deg, transparent 58%, rgba(24,31,41,.18) 100%)"
                pointerEvents="none"
                aria-hidden="true"
              />
            </Box>
          </MaskedReveal>
        </Grid>
      </Box>
    </Box>
  );
};

export default HomeStoryIntro;

